/**
 * Configuração centralizada do Apollo Client para GraphQL
 *
 * - Define endpoint, cache e envio automático do JWT
 * - Deve ser importado e usado no ApolloProvider no topo da aplicação
 * - Reutilizável para todos os módulos/domínios GraphQL
 */
import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { print } from "graphql";
import * as Sentry from "@sentry/react";
import { toast } from "react-hot-toast";
import { logout } from "../services/auth/auth.service";
import { buildApiUrl } from "./config";
  
const resolvedGraphqlUrl = buildApiUrl("graphql");

const httpLink = new HttpLink({
  uri: resolvedGraphqlUrl,
});

// Link customizado para uploads - converte File para multipart/form-data
// Segue a especificação GraphQL Multipart Request: https://github.com/jaydenseric/graphql-multipart-request-spec
// Esta implementação manual é necessária porque o Apollo Client não serializa File objects nativamente
const uploadLink = new ApolloLink((operation, forward) => {
  // Verificar se a operação contém algum File nas variáveis
  const hasFile = Object.values(operation.variables || {}).some(
    (value) => value instanceof File
  );

  // Se não tiver File, passar para o próximo link (httpLink)
  if (!hasFile) {
    return forward(operation);
  }

  // Se tiver File, criar FormData seguindo a especificação GraphQL Multipart Request
  const formData = new FormData();

  // Mapear arquivos para o map necessário
  // O map relaciona o índice do arquivo no FormData com o caminho na variável GraphQL
  const map: Record<string, string[]> = {};
  let fileIndex = 0;

  // Criar cópia das variáveis, substituindo Files por null
  // Conforme a especificação, Files devem ser null nas variáveis JSON e referenciados no map
  const variablesWithoutFiles: Record<string, unknown> = {};

  // Processar variáveis: copiar valores normais e substituir Files por null
  Object.entries(operation.variables || {}).forEach(([key, value]) => {
    if (value instanceof File) {
      // Adicionar arquivo ao FormData com índice numérico
      const fileKey = `${fileIndex}`;
      formData.append(fileKey, value);

      // Mapear o índice do arquivo para o caminho da variável
      map[fileKey] = [`variables.${key}`];

      // Substituir File por null nas variáveis (especificação requer null)
      variablesWithoutFiles[key] = null;

      fileIndex++;
    } else {
      // Copiar variável normal sem modificação
      variablesWithoutFiles[key] = value;
    }
  });

  // Construir operations JSON conforme especificação
  // operations contém: query (string), variables (com Files = null), operationName (opcional)
  const operations = {
    query: operation.query ? print(operation.query) : "",
    variables: variablesWithoutFiles,
    operationName: operation.operationName || undefined,
  };

  // Adicionar operations como string JSON
  formData.append("operations", JSON.stringify(operations));

  // Adicionar map como string JSON
  // O map relaciona cada arquivo no FormData ao seu caminho nas variáveis
  formData.append("map", JSON.stringify(map));

  // Obter token de autenticação do contexto da operação ou localStorage
  const token =
    operation.getContext().headers?.Authorization?.replace("Bearer ", "") ||
    localStorage.getItem("access_token");

  // Fazer fetch manual para upload multipart/form-data
  return new Observable((observer) => {
    fetch(resolvedGraphqlUrl, {
      method: "POST",
      headers: {
        // Authorization será adicionado pelo authLink normalmente, mas como estamos fazendo fetch manual,
        // precisamos adicionar aqui também
        Authorization: token ? `Bearer ${token}` : "",
        // Header necessário para bypass de CSRF no Apollo Server 4.x para uploads multipart
        "apollo-require-preflight": "true",
        // NÃO definir Content-Type - o browser define automaticamente com boundary para multipart/form-data
        // Definir Content-Type manualmente quebra o boundary
      },
      body: formData,
    })
      .then(async (response) => {
        // Verificar se a resposta tem conteúdo JSON
        const contentType = response.headers.get("content-type");
        if (!contentType?.includes("application/json")) {
          const text = await response.text();
          observer.error({
            networkError: {
              statusCode: response.status,
              result: text,
              message: `Resposta inválida do servidor: ${response.status}`,
            },
          });
          return;
        }

        const result = await response.json();

        // Verificar erros HTTP (4xx, 5xx)
        if (!response.ok) {
          observer.error({
            networkError: {
              statusCode: response.status,
              result,
              message: result.message || `Erro HTTP ${response.status}`,
            },
            graphQLErrors: result.errors || [],
          });
          return;
        }

        // Verificar erros GraphQL (mesmo com HTTP 200)
        if (result.errors && result.errors.length > 0) {
          observer.error({
            graphQLErrors: result.errors,
            networkError: undefined,
          });
          return;
        }

        // Sucesso: retornar dados
        observer.next({
          data: result.data,
        });
        observer.complete();
      })
      .catch((_error) => {
        observer.error({
          networkError: {
            ..._error,
            message: _error.message || "Erro de rede ao fazer upload",
          },
        });
      });
  });
});

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward: _forward }) => {
    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        // Enviar erros GraphQL para o Sentry (exceto erros esperados)
        const expectedErrorMessages = [
          "CPF já cadastrado",
          "Email já cadastrado",
          "Matrícula já existe",
          "Usuário já existe",
          // Erros de campos null que são esperados em certos cenários
          "Nao existem pareceres registrados",
          "Matriz curricular não encontrada",
        ];

        // Erros de campos non-nullable que retornam null - geralmente problema de dados no backend
        // Não devemos logar como erro crítico, apenas warning
        const nullFieldErrors = [
          "Cannot return null for non-nullable field EstatisticasRegistroDiarioType.totalRegistros",
          "Cannot return null for non-nullable field GradeHorariaType.alocacao",
          "Cannot return null for non-nullable field AvaliacaoType.tipo",
        ];

        const isNullFieldError = nullFieldErrors.some((msg) =>
          err.message.includes(msg)
        );

        // Detectar erros de campo null em avaliações e agendar limpeza de cache
        if (
          err.message.includes(
            "Cannot return null for non-nullable field AvaliacaoType.tipo"
          )
        ) {
          console.warn(
            "Detectado erro de AvaliacaoType.tipo null, agendando limpeza de cache..."
          );
          // Agendar limpeza de cache para depois da inicialização
          setTimeout(() => {
            if (apolloClient?.cache) {
              apolloClient.cache.evict({ fieldName: "notas" });
              apolloClient.cache.evict({ fieldName: "avaliacoes" });
              apolloClient.cache.gc();
            }
          }, 100);
        }

        // Detectar erros de campo null em estatísticas e grade horária - apenas log de warning
        if (isNullFieldError) {
          // Não enviar para Sentry como erro, apenas como breadcrumb informativo
          Sentry.addBreadcrumb({
            category: "graphql",
            message: `Null field warning: ${err.message}`,
            level: "warning",
            data: {
              operation: operation.operationName,
              path: err.path?.join("."),
            },
          });
          continue; // Não processar como erro crítico
        }

        if (
          err.extensions?.code !== "UNAUTHENTICATED" &&
          !expectedErrorMessages.some((msg) => err.message.includes(msg))
        ) {
          Sentry.withScope((scope) => {
            scope.setTag("errorType", "GraphQL");
            scope.setLevel("error");
            scope.setContext("GraphQL Error", {
              message: err.message,
              locations: err.locations,
              path: err.path,
              code: err.extensions?.code,
              operation: operation.operationName,
              variables: operation.variables,
            });
            Sentry.captureException(new Error(`GraphQL Error: ${err.message}`));
          });
        }

        if (
          err.extensions?.code === "UNAUTHENTICATED" ||
          (err.extensions?.originalError as any)?.statusCode === 401
        ) {
          // Limpar dados de autenticação e redirecionar para o login
          logout();
          window.location.href = "/login";
        } else if (
          err.extensions?.code === "FORBIDDEN" ||
          (err.extensions?.originalError as any)?.statusCode === 403
        ) {
          // Acesso negado: notificar usuário, sem deslogar
          toast.error("Acesso negado.");
        }
      }
    }

    if (networkError) {
      const statusCode =
        "statusCode" in networkError ? networkError.statusCode : undefined;
      const errorMessage = networkError.message?.toLowerCase() || "";

      // Verificar se é erro de autenticação (401 ou mensagem "Unauthorized")
      const isUnauthorized =
        statusCode === 401 || errorMessage.includes("unauthorized");

      // Não enviar erros de autenticação para o Sentry - são esperados quando sessão expira
      if (isUnauthorized) {
        Sentry.addBreadcrumb({
          category: "auth",
          message: "Session expired - redirecting to login",
          level: "info",
          data: {
            operation: operation.operationName,
            statusCode,
          },
        });
        logout();
        window.location.href = "/login";
        return; // Não processar mais nada
      }

      // Enviar network errors para o Sentry (400, 404, 500, etc.) - exceto 401/403
      if (statusCode && statusCode >= 400 && statusCode !== 403) {
        Sentry.withScope((scope) => {
          scope.setTag("errorType", "Network");
          scope.setLevel("error");
          scope.setContext("Network Error", {
            statusCode,
            message: networkError.message,
            operation: operation.operationName,
            variables: operation.variables,
            url: resolvedGraphqlUrl,
          });
          Sentry.captureException(
            new Error(
              `GraphQL Network Error: ${statusCode} - ${networkError.message}`
            )
          );
        });
      }

      if (statusCode === 403) {
        // Acesso negado em nível de rede: notificar usuário
        toast.error("Acesso negado.");
      }

      // Tratar erros de rede genéricos (Failed to fetch)
      if (errorMessage.includes("failed to fetch")) {
        // Não enviar para Sentry se for falha de conectividade comum
        Sentry.addBreadcrumb({
          category: "network",
          message: "Connection failed",
          level: "warning",
          data: {
            operation: operation.operationName,
            message: networkError.message,
          },
        });
      }
    }
  }
);

const authLink = new ApolloLink((operation, forward) => {
  const token = localStorage.getItem("access_token");
  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : "",
    },
  }));
  return forward(operation);
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink, uploadLink, httpLink]),
  cache: new InMemoryCache(),
});
